/**
 * Author: Valeria Sinnati
 *  ∧,,,∧
 * ( ̳• · • ̳)   Sessione del consumatore (M19): access token in memoria,
 * /    づ    rinnovo dal cookie di refresh.
 *
 * Il token NON va in `localStorage`: sopravviverebbe alla scheda chiusa su un
 * telefono che passa di mano al tavolo accanto. Il cookie di refresh è
 * `HttpOnly` e vale su `app.<dominio>` e `account.<dominio>` — chi accede dal
 * menu resta autenticato nell'area account e viceversa.
 */
'use client'

export type ClienteMe = {
  id: string
  phone: string | null
  email: string | null
  nome: string | null
  creato_il: string
}

export type OrdineCliente = {
  id: string
  ristorante: string
  ristorante_slug: string
  stato: string
  totale: number
  created_at: string
  items: { nome: string; qty: number; prezzo_unitario: number; note: string | null }[]
}

let accessToken: string | null = null
let rinnovoInCorso: Promise<string | null> | null = null

/** Nome del cookie CSRF (non `HttpOnly`: deve leggerlo il JS, per definizione). */
const COOKIE_CSRF = 'csrf_cliente'

/**
 * Token CSRF dal cookie, da rimandare nell'header sulle richieste che scrivono.
 *
 * È il cuore del double-submit: il browser manda il cookie da solo anche a una
 * richiesta partita da un sito ostile, ma quel sito il cookie NON può leggerlo,
 * quindi non riesce a compilare l'header. Il server pretende entrambi.
 */
function tokenCsrf(): string | null {
  if (typeof document === 'undefined') return null
  const trovato = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_CSRF}=`))
  return trovato ? decodeURIComponent(trovato.slice(COOKIE_CSRF.length + 1)) : null
}

export function tokenCorrente(): string | null {
  return accessToken
}

/**
 * C'è una sessione da ripristinare?
 *
 * Il token sta in memoria e si perde a ogni caricamento di pagina; il cookie
 * di refresh è `HttpOnly` e il JS non lo vede. Quello CSRF però sì — non è
 * `HttpOnly` per definizione — e viene messo e tolto insieme al refresh:
 * basta lui per sapere se vale la pena chiedere un rinnovo, invece di sparare
 * una POST inutile per ogni ospite che apre il menu al tavolo.
 */
export function sessioneDaRipristinare(): boolean {
  return accessToken === null && tokenCsrf() !== null
}

export function impostaToken(token: string | null): void {
  accessToken = token
}

/**
 * Rinnova l'access token dal cookie.
 *
 * Le chiamate concorrenti condividono la stessa promessa: al caricamento della
 * pagina partono insieme il ripristino della sessione e la lettura degli
 * ordini, e due rinnovi in parallelo ruoterebbero lo stesso refresh token —
 * che il backend interpreta come furto e revoca l'intera famiglia (08 §1).
 */
export async function rinnova(): Promise<string | null> {
  if (rinnovoInCorso) return rinnovoInCorso
  rinnovoInCorso = (async () => {
    try {
      const resp = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      })
      if (!resp.ok) return null
      const dati = (await resp.json()) as { access_token: string }
      accessToken = dati.access_token
      return accessToken
    } catch {
      return null
    } finally {
      rinnovoInCorso = null
    }
  })()
  return rinnovoInCorso
}

/** `fetch` con il token del cliente, che ritenta UNA volta dopo il rinnovo. */
export async function fetchCliente(url: string, init: RequestInit = {}): Promise<Response> {
  const csrf = tokenCsrf()
  const conToken = (token: string | null): RequestInit => ({
    ...init,
    credentials: 'include',
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Solo dove serve: sulle letture il server non lo guarda
      ...(csrf && init.method && init.method !== 'GET' ? { 'X-CSRF-Token': csrf } : {}),
    },
  })

  let resp = await fetch(url, conToken(accessToken))
  if (resp.status === 401) {
    const nuovo = await rinnova()
    if (nuovo) resp = await fetch(url, conToken(nuovo))
  }
  return resp
}

export async function chiediCodice(phone: string): Promise<boolean> {
  const resp = await fetch('/api/v1/public/customer/otp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  })
  return resp.ok
}

export async function verificaCodice(
  phone: string,
  codice: string,
  opzioni: { nome?: string; tenantSlug?: string } = {},
): Promise<boolean> {
  const resp = await fetch('/api/v1/public/customer/verify', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone,
      codice,
      nome: opzioni.nome || null,
      tenant_slug: opzioni.tenantSlug || null,
    }),
  })
  if (!resp.ok) return false
  const dati = (await resp.json()) as { access_token: string }
  accessToken = dati.access_token
  return true
}

export async function mioProfilo(): Promise<ClienteMe | null> {
  const resp = await fetchCliente('/api/v1/customer/me')
  return resp.ok ? ((await resp.json()) as ClienteMe) : null
}

export async function mieiOrdini(): Promise<OrdineCliente[]> {
  const resp = await fetchCliente('/api/v1/customer/orders')
  return resp.ok ? ((await resp.json()) as OrdineCliente[]) : []
}

export async function esci(): Promise<void> {
  await fetchCliente('/api/v1/customer/logout', { method: 'POST' })
  accessToken = null
}
